import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchWorkspaceUsers,
  resendWorkspaceUserInvite,
  updateWorkspaceUser,
} from '../../api/workspaceUsers';
import { StatusBanner } from '../../components/ui';
import {
  ASSIGN_USERS_NEW_PATH,
  ASSIGN_USERS_PATH,
  StatCard,
  UserTable,
  assignUserEditPath,
} from './assignUserShared';

const PREVIEW_LIMIT = 5;

function AssignUserSettings({ token, onClose }) {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({ type: 'idle', message: '' });

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setStatus({ type: 'idle', message: '' });
    try {
      const payload = await fetchWorkspaceUsers(token, { status: 'all' });
      setUsers(payload.users || []);
      setStats(payload.stats || { total: 0, active: 0, inactive: 0 });
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Unable to load users.',
      });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const previewUsers = useMemo(() => users.slice(0, PREVIEW_LIMIT), [users]);

  const goToPage = (path) => {
    if (typeof onClose === 'function') {
      onClose();
    }
    navigate(path);
  };

  const handleToggleActive = async (user) => {
    setStatus({ type: 'idle', message: '' });
    try {
      await updateWorkspaceUser(token, user.id, { isActive: !user.isActive });
      await loadUsers();
      setStatus({
        type: 'success',
        message: user.isActive ? 'User deactivated.' : 'User activated.',
      });
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Unable to update user status.',
      });
    }
  };

  const handleResendInvite = async (user) => {
    setStatus({ type: 'idle', message: '' });
    try {
      const payload = await resendWorkspaceUserInvite(token, user.id);
      const sent = payload?.invite?.sent === true;
      setStatus({
        type: sent ? 'success' : 'error',
        message: sent
          ? `Invite resent to ${user.email} (valid 12 hours).`
          : payload?.invite?.error || 'Invite could not be sent. Check SMTP configuration.',
      });
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Unable to resend invite.',
      });
    }
  };

  return (
    <div className="grid gap-4">
      {status.type !== 'idle' && status.message ? (
        <StatusBanner status={status} setStatus={setStatus} />
      ) : null}

      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard label="Total users" value={stats.total} />
        <StatCard label="Active" value={stats.active} tone="good" />
        <StatCard label="Inactive" value={stats.inactive} tone="muted" />
      </div>

      <div className="rounded-xl border border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-surface))] overflow-hidden shadow-xs">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-surface))] px-5 py-4">
          <div>
            <div className="flex items-center gap-2">
              <h4 className="text-base font-bold text-[rgb(var(--crm-text))]">Assigned users</h4>
              <span className="rounded-full border border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-page-bg))] px-2 py-0.5 text-xs font-semibold text-[rgb(var(--crm-text-muted))]">
                {stats.total}
              </span>
            </div>
            <p className="mt-1 text-xs text-[rgb(var(--crm-text-muted))]">
              Staff logins with limited section access under your seller account.
            </p>
          </div>
          <button
            type="button"
            className="flex items-center gap-1.5 rounded-lg bg-[rgb(var(--crm-accent))] px-3.5 py-2 text-xs font-semibold text-white shadow-xs transition hover:bg-[rgb(var(--crm-accent-hover))]"
            onClick={() => goToPage(ASSIGN_USERS_NEW_PATH)}
          >
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            <span>Create user</span>
          </button>
        </div>

        <UserTable
          users={previewUsers}
          loading={loading}
          emptyLabel="No users yet. Create one to share limited access."
          onEdit={(user) => goToPage(assignUserEditPath(user.id))}
          onToggleActive={handleToggleActive}
          onResendInvite={handleResendInvite}
        />

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-page-bg))]/30 px-5 py-3">
          <p className="text-xs text-[rgb(var(--crm-text-muted))]">
            Showing {previewUsers.length} of {users.length} user
            {users.length === 1 ? '' : 's'}
          </p>
          <button
            type="button"
            className="flex items-center gap-1.5 rounded-lg border border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-surface))] px-3 py-1.5 text-xs font-semibold text-[rgb(var(--crm-text))] shadow-xs transition hover:bg-[rgb(var(--crm-page-bg))] hover:border-[rgb(var(--crm-accent)/0.3)]"
            onClick={() => goToPage(ASSIGN_USERS_PATH)}
          >
            <span>View all users</span>
            <svg className="h-3 w-3 text-[rgb(var(--crm-text-muted))]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>

      <div className="flex items-center gap-1.5 text-[11px] text-[rgb(var(--crm-text-muted))]">
        <svg className="h-3.5 w-3.5 text-[rgb(var(--crm-accent))]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
        <span>"Create user" and "View all users" navigate to the dedicated management page.</span>
      </div>
    </div>
  );
}

export default AssignUserSettings;
